/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Stack,
    Typography,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Table,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import Autocomplete from "@mui/material/Autocomplete";

import {
    InventoryItem,
    Material,
    Nullable,
    StorageLocation,
    StorageLocationDetailed,
    TechwiseOffice,
    TechwiseOfficeDetailed,
} from "../../utils/Interfaces";

import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import { techwiseOfficeDetails } from "../materials-techwise-office/techwiseOfficeFunctions";
import { updateTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesFunctions";
import { NumericFormat } from "react-number-format";
import { CompareArrows } from "@mui/icons-material";
import { inventoryTransfer } from "./materialTransferFunctions";
import { storageLocationDetails } from "../materials-storage-location/storageLocationFunctions";
import { customScrollbarStyle, delayedAction } from "../../utils/Functions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import DeferredAttachmentManager from "../../components/general/DeferredAttachmentManager";
import { ModelName } from "../../utils/Requests";
import AnimatedCompletionCheck from "../../components/confirmation/AnimatedCompletionCheck";

const InventoryTransfers = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);
    const [selectedFromTechwiseOffice, setSelectedFromTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);
    const [selectedToTechwiseOffice, setSelectedToTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);

    const [fromStorageLocations, setFromStorageLocations] = useState<StorageLocation[]>([]);
    const [toStorageLocations, setToStorageLocations] = useState<StorageLocation[]>([]);
    const [selectedFromStorageLocation, setSelectedFromStorageLocation] = useState<Nullable<StorageLocation>>(null);
    const [selectedToStorageLocation, setSelectedToStorageLocation] = useState<Nullable<StorageLocation>>(null);

    const [fromInventories, setFromInventories] = useState<InventoryItem[]>([]);
    const [selectedFromInventory, setSelectedFromInventory] = useState<Nullable<InventoryItem>>(null);

    const [toInventories, setToInventories] = useState<InventoryItem[]>([]);

    const [amount, setAmount] = useState<number>(0);
    const [transferNotes, setTransferNotes] = useState<string>("");

    const [officesLoaded, setOfficesLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [complete, setComplete] = useState<boolean>(false);

    const [inventoryTransferId, setInventoryTransferId] = useState<Nullable<number>>(null);

    function getFormErrors(): string {
        if (techwiseOffices.length === 0) {
            return "No Techwise offices available.";
        }

        if (fromStorageLocations.length === 0) {
            return "No origin storage locations available.";
        }

        if (toStorageLocations.length === 0) {
            return "No destination storage locations available.";
        }

        if (selectedFromStorageLocation === null) {
            return "No selected origin storage location.";
        }

        if (selectedToStorageLocation === null) {
            return "No selected destination storage location.";
        }

        if (selectedFromStorageLocation.id === selectedToStorageLocation.id) {
            return "Source and destination storage locations cannot be the same.";
        }

        if (selectedFromInventory === null) {
            return "Please select an inventory to continue.";
        }

        if (amount === null || amount < 1 || isNaN(amount)) {
            return "Amount to transfer must be at least 1.";
        }

        if (amount > selectedFromInventory.amount) {
            return "Amount requested to transfer is more than the source location contains.";
        }

        return "";
    }

    // Logic to disable the complete check after a set period of time.
    useEffect(() => {
        if (complete) {
            delayedAction(2000, () => setComplete(false));
        }
    }, [complete]);

    useEffect(() => {
        setLoading(true);
        if (techwiseOffices.length > 0) {
            setSelectedFromTechwiseOffice(techwiseOffices[0]);
            setSelectedToTechwiseOffice(techwiseOffices[0]);
        } else {
            // If the count of offices is 0, attempt a single reload. Use a Boolean flag to prevent recursive requests.
            if (!officesLoaded) {
                setOfficesLoaded(true);
                updateTechwiseOffices(dispatch, () => {});
            }
        }
    }, [techwiseOffices]);

    useEffect(() => {
        setSelectedFromStorageLocation(null);
        setFromInventories([]);
        if (selectedFromTechwiseOffice !== null) {
            techwiseOfficeDetails(selectedFromTechwiseOffice?.id, dispatch, (techwiseOfficeDetailed: TechwiseOfficeDetailed) => {
                let nonDefectiveLocations = techwiseOfficeDetailed.locations.filter(
                    (location) => !location.location_type.toLowerCase().includes("defective")
                );
                setFromStorageLocations(nonDefectiveLocations);
                if (nonDefectiveLocations.length > 0) {
                    setSelectedFromStorageLocation(nonDefectiveLocations[0]);
                }
                setLoading(false);
            });
        }
    }, [selectedFromTechwiseOffice]);

    useEffect(() => {
        setSelectedToStorageLocation(null);
        if (selectedToTechwiseOffice !== null) {
            techwiseOfficeDetails(selectedToTechwiseOffice?.id, dispatch, (techwiseOfficeDetailed: TechwiseOfficeDetailed) => {
                let nonDefectiveLocations = techwiseOfficeDetailed.locations.filter(
                    (location) => !location.location_type.toLowerCase().includes("defective")
                );
                setToStorageLocations(nonDefectiveLocations);
                if (nonDefectiveLocations.length > 0) {
                    setSelectedToStorageLocation(nonDefectiveLocations[0]);
                }
                setLoading(false);
            });
        }
    }, [selectedToTechwiseOffice]);

    useEffect(() => {
        setSelectedFromInventory(null);
        if (selectedFromStorageLocation !== null) {
            storageLocationDetails(
                selectedFromStorageLocation?.id,
                dispatch,
                (storageLocationDetailed: StorageLocationDetailed) => {
                    let filteredFromInventories: InventoryItem[] = storageLocationDetailed.inventory.filter(
                        (inventory: InventoryItem) => inventory.amount > 0
                    );
                    setFromInventories(filteredFromInventories);
                    if (storageLocationDetailed.inventory.length > 0) {
                        setSelectedFromInventory(filteredFromInventories[0]);
                    }
                }
            );
        }
    }, [selectedFromStorageLocation]);

    useEffect(() => {
        if (selectedToStorageLocation !== null) {
            storageLocationDetails(
                selectedToStorageLocation?.id,
                dispatch,
                (storageLocationDetailed: StorageLocationDetailed) => {
                    setToInventories(storageLocationDetailed.inventory);
                }
            );
        }
    }, [selectedToStorageLocation]);

    if (loading) {
        return (
            <Box sx={{ mt: 5 }}>
                <Stack direction="row" justifyContent="center" sx={{ mt: 15, mb: 10 }}>
                    <CircularProgress size={40} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            {getFormErrors() === "" && (
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                    All entries are valid.
                </Alert>
            )}
            {getFormErrors() !== "" && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {getFormErrors()}
                </Alert>
            )}
            <Grid spacing={3} container>
                <Grid item xs={6}>
                    <Typography variant="h6" sx={{ ml: 0.5 }}>
                        From
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6">To</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedFromTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                        }}
                        id="techwiseOfficesFrom"
                        getOptionKey={(option: TechwiseOffice) => option.id}
                        options={techwiseOffices}
                        value={selectedFromTechwiseOffice}
                        getOptionLabel={(option: TechwiseOffice) => option.name}
                        renderInput={(params) => <TextField required {...params} label="From Techwise Office" />}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedToTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                        }}
                        id="techwiseOfficesTo"
                        getOptionKey={(option: TechwiseOffice) => option.id}
                        options={techwiseOffices}
                        value={selectedToTechwiseOffice}
                        getOptionLabel={(option: TechwiseOffice) => option.name}
                        renderInput={(params) => <TextField required {...params} label="To Techwise Office" />}
                    />
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedFromStorageLocation(
                                value ? value : fromStorageLocations.length > 0 ? fromStorageLocations[0] : null
                            );
                        }}
                        id="fromStorageLocations"
                        getOptionKey={(option: StorageLocation) => option.id}
                        options={fromStorageLocations}
                        value={selectedFromStorageLocation}
                        getOptionLabel={(option: StorageLocation) => option.name}
                        renderInput={(params) => <TextField required {...params} label="From Storage Locations" />}
                    />
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedToStorageLocation(
                                value ? value : toStorageLocations.length > 0 ? toStorageLocations[0] : null
                            );
                        }}
                        id="toStorageLocations"
                        getOptionKey={(option: StorageLocation) => option.id}
                        options={toStorageLocations}
                        value={selectedToStorageLocation}
                        getOptionLabel={(option: StorageLocation) => option.name}
                        renderInput={(params) => <TextField required {...params} label="To Storage Locations" />}
                    />
                </Grid>
            </Grid>

            <Grid spacing={3} sx={{ mt: 1 }} container>
                <Grid item xs={6}>
                    <Box>
                        <Autocomplete
                            disablePortal
                            disabled={fromInventories.length === 0}
                            onChange={(_event, value) => {
                                setSelectedFromInventory(value ? value : fromInventories.length > 0 ? fromInventories[0] : null);
                            }}
                            id="inventories"
                            getOptionKey={(option: InventoryItem) => option.id}
                            options={fromInventories}
                            value={selectedFromInventory}
                            getOptionLabel={(option: InventoryItem) =>
                                `${option.material.name} (${option.material.part_number}) | (${option.amount})`
                            }
                            renderInput={(params) => (
                                <TextField
                                    required
                                    {...params}
                                    label="Inventory"
                                    helperText="Material Name (Part #) | (Amt. Available)"
                                />
                            )}
                        />
                        <Box sx={{ mt: 3 }} />
                        <NumericFormat
                            disabled={selectedFromInventory === null}
                            value={amount}
                            customInput={TextField}
                            style={{ marginLeft: 0, marginTop: 0, width: "100%" }}
                            label="Amount"
                            thousandSeparator=","
                            decimalScale={0}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            onValueChange={(values) => {
                                setAmount(parseInt(values.value));
                            }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            sx={{ mt: 3 }}
                            value={transferNotes}
                            onChange={(event) => setTransferNotes(event.target.value)}
                            label="Transfer Notes"
                            name="transfer_notes"
                        />
                        <DeferredAttachmentManager
                            model={ModelName.InventoryTransfer}
                            idKey={inventoryTransferId}
                            readOnly={false}
                            editing={true}
                            onFilesUploaded={() => {
                                // Set the selected storage location as a new shallow copy. This will re-trigger the
                                // useEffect and update the inventory after a transfer is complete.
                                setSelectedFromStorageLocation({ ...selectedFromStorageLocation } as StorageLocation);
                                setSelectedToStorageLocation({ ...selectedToStorageLocation } as StorageLocation);

                                setComplete(true);
                                setAmount(0);
                                setTransferNotes("");
                                setSubmitting(false);
                                setInventoryTransferId(null);
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <TableContainer
                        sx={{
                            mt: -0.1,
                            maxHeight: 460,
                            overflowY: "auto",
                            overflowX: "hidden",
                            ...customScrollbarStyle(),
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography fontWeight="bold">Name</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">Part #</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">Amount</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">Target Amount</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {toInventories.map((inventory: InventoryItem, index: number) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Typography>{inventory.material.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{inventory.material.part_number}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{inventory.amount}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{inventory.target}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {submitting && <CircularProgress size={16} sx={{ mt: 6, ml: 2, mb: 0.8 }} />}

            {!submitting && (
                <Stack direction="row" justifyContent="flex-start" sx={{ mt: 5 }}>
                    <Button
                        variant="contained"
                        disabled={getFormErrors() !== "" || submitting}
                        onClick={() => {
                            setSubmitting(true);
                            inventoryTransfer(
                                selectedFromStorageLocation as StorageLocation,
                                selectedToStorageLocation as StorageLocation,
                                selectedFromInventory?.material as Material,
                                amount,
                                transferNotes,
                                dispatch,
                                (completedTransferId: number) => {
                                    setInventoryTransferId(completedTransferId);
                                },
                                () => setSubmitting(false)
                            );
                        }}
                    >
                        <CompareArrows />
                        <Typography sx={{ ml: 1 }}>Initiate Transfer</Typography>
                    </Button>
                    {complete && (
                        <Stack direction="row" justifyContent="flex-start" sx={{ mt: 1, ml: 3 }}>
                            <AnimatedCompletionCheck size={24} />
                            <Typography color="success.main" sx={{ ml: 2, mt: -0.35 }} variant="h6">
                                Transfer Successful
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default InventoryTransfers;
